import { useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { baseURL } from '../config';
import AuthContext from '../Context/AuthContext';
import Popup from './Popup';
import AssetDropzone from './AssetDropzone';
import ButtonSolid from './ButtonSolid';
import ButtonOutline from './ButtonOutline';
import LoadingAnimation from './LoadingAnimation';
import { CloseIcon } from './Icons';
import { STAGE_LABEL, replaceAsset, showsProgress } from '../utils/assetUpload';

// Replaces the file of an asset in place (same asset, so every assembly using it gets
// the new file). If the new file already exists as another asset of the same type,
// the two can be merged: assemblies are switched to the existing asset and this one
// is deleted.
function ReplaceAssetModal({ asset, onClose, onChanged }) {
    const { token, logout } = useContext(AuthContext);
    const [usage, setUsage] = useState({ loading: true, count: 0 });
    const [pendingFile, setPendingFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [stage, setStage] = useState(null);
    const [progress, setProgress] = useState(0);
    const [duplicate, setDuplicate] = useState(null); // the existing asset holding the same file
    const [merging, setMerging] = useState(false);
    const abortRef = useRef(null);

    useEffect(() => {
        const fetchUsage = async () => {
            try {
                const response = await axios.get(`${baseURL}/asset/${asset._id}/usage`, {
                    headers: { 'x-access-token': token },
                });
                setUsage({ loading: false, count: (response.data.data || []).length });
            } catch (error) {
                if (error.response?.status === 401) logout();
                setUsage({ loading: false, count: 0 });
            }
        };
        fetchUsage();
    }, [asset._id, token, logout]);

    const plural = usage.count === 1 ? 'assembly' : 'assemblies';

    const close = () => {
        if (uploading || merging) return;
        onClose();
    };

    const submitReplace = async () => {
        const controller = new AbortController();
        abortRef.current = controller;
        try {
            setUploading(true);
            setProgress(0);
            const result = await replaceAsset({
                file: pendingFile,
                assetId: asset._id,
                type: asset.type,
                token,
                signal: controller.signal,
                onStage: setStage,
                onProgress: setProgress,
            });
            if (result.duplicate) {
                setDuplicate(result.asset);
            } else {
                toast.success('Asset replaced successfully');
                onChanged?.();
                onClose();
            }
        } catch (error) {
            if (axios.isCancel(error) || error.code === 'ERR_CANCELED') {
                toast.error('Replace cancelled');
            } else if (error.response?.status === 401) {
                logout();
            } else {
                toast.error(error.response?.data?.message || error.message || 'Error replacing asset');
            }
        } finally {
            setUploading(false);
            setStage(null);
            abortRef.current = null;
        }
    };

    const mergeIntoExisting = async () => {
        try {
            setMerging(true);
            await axios.post(`${baseURL}/asset/${asset._id}/mergeInto/${duplicate._id}`, {}, {
                headers: { 'x-access-token': token },
            });
            toast.success(`Merged into "${duplicate.name}"`);
            onChanged?.();
            onClose();
        } catch (error) {
            if (error.response?.status === 401) {
                logout();
            } else {
                toast.error(error.response?.data?.message || error.message || 'Error merging assets');
            }
        } finally {
            setMerging(false);
        }
    };

    const chooseDifferentFile = () => {
        setDuplicate(null);
        setPendingFile(null);
    };

    return (
        <Popup maxWidth='md:max-w-[500px]'>
            <div className='p-2'>
                <div className='flex justify-between items-center mb-4'>
                    <p className='text-lg font-semibold text-gray-700'>Replace Asset</p>
                    <button
                        type='button'
                        disabled={uploading || merging}
                        onClick={close}
                        className='p-2 border-2 bg-white border-gray-300 rounded-md cursor-pointer disabled:cursor-not-allowed'
                    >
                        <CloseIcon size='w-5 h-5' />
                    </button>
                </div>

                {usage.loading ? (
                    <LoadingAnimation />
                ) : duplicate ? (
                    duplicate.type === asset.type ? (
                        <div>
                            <p className='text-sm border-dashed border-2 border-yellow-500 bg-yellow-50 p-2 rounded-md mb-3'>
                                An identical file already exists as <span className='font-medium'>"{duplicate.name}"</span> ({duplicate.type}).
                            </p>
                            <p className='text-sm text-gray-600 mb-4'>
                                {usage.count > 0 ? (
                                    <>
                                        Merging switches the {usage.count} {plural} using <span className='font-medium'>"{asset.name}"</span> over
                                        to <span className='font-medium'>"{duplicate.name}"</span> and deletes <span className='font-medium'>"{asset.name}"</span>.
                                    </>
                                ) : (
                                    <>Merging deletes <span className='font-medium'>"{asset.name}"</span>, which no assembly uses.</>
                                )}{' '}
                                This can't be undone.
                            </p>
                            <div className='flex flex-col md:flex-row-reverse gap-4'>
                                <ButtonSolid buttonText='Merge into Existing' onClick={mergeIntoExisting} isLoading={merging} loadingText='Merging' className='w-full' />
                                <ButtonOutline buttonText='Cancel' onClick={close} disabled={merging} className='w-full' />
                            </div>
                        </div>
                    ) : (
                        <div>
                            <p className='text-sm border-dashed border-2 border-yellow-500 bg-yellow-50 p-2 rounded-md mb-4'>
                                An identical file already exists as <span className='font-medium'>"{duplicate.name}"</span>, but it is a different type
                                ({duplicate.type}), so the two can't be merged. Choose a different file.
                            </p>
                            <div className='flex flex-col md:flex-row-reverse gap-4'>
                                <ButtonSolid buttonText='Choose Different File' onClick={chooseDifferentFile} className='w-full' />
                                <ButtonOutline buttonText='Cancel' onClick={close} className='w-full' />
                            </div>
                        </div>
                    )
                ) : (
                    <>
                        <p className='text-sm border-dashed border-2 border-red-500 bg-red-100 p-2 rounded-md mb-4'>
                            <span className='text-red-600'>Warning:</span> "{asset.name}" is used by {usage.count} {plural}. Replacing it updates the file for all of them.
                        </p>
                        {!pendingFile ? (
                            <AssetDropzone
                                disabled={uploading}
                                lockedType={asset.type}
                                continueLabel='Continue to Replace'
                                onFileSelected={(file) => setPendingFile(file)}
                            />
                        ) : uploading ? (
                            <div>
                                <p className='text-sm text-gray-600 mb-2'>
                                    {STAGE_LABEL[stage] || 'Working...'} <span className='font-medium'>{pendingFile.name}</span>
                                </p>
                                {showsProgress(stage) && (
                                    <>
                                        <div className='w-full bg-gray-200 rounded-full h-2 mb-2'>
                                            <div className='bg-orange-400 h-2 rounded-full transition-all' style={{ width: `${progress}%` }} />
                                        </div>
                                        <p className='text-xs text-gray-400 mb-4'>{progress}%</p>
                                    </>
                                )}
                                {!showsProgress(stage) && <div className='mb-4' />}
                                <ButtonOutline buttonText='Cancel Upload' onClick={() => abortRef.current?.abort()} className='w-full' />
                            </div>
                        ) : (
                            <div className='mt-4'>
                                <p className='text-sm text-gray-600 mb-4'>
                                    Replace <span className='font-medium'>{asset.name}</span> with{' '}
                                    <span className='font-medium'>{pendingFile.name}</span> for all {usage.count} {plural}?
                                </p>
                                <div className='flex flex-col md:flex-row-reverse gap-4'>
                                    <ButtonSolid buttonText='Replace' onClick={submitReplace} className='w-full' />
                                    <ButtonOutline buttonText='Choose Different File' onClick={() => setPendingFile(null)} className='w-full' />
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </Popup>
    );
}

export default ReplaceAssetModal;
